/**
 * Client-side Rate Limiter
 * Uses localStorage to track request counts per endpoint/action
 */

// Rate limit configurations
export const RATE_LIMITS = {
  // Authentication
  LOGIN: { maxRequests: 5, windowMs: 15 * 60 * 1000 }, // 5 attempts per 15 minutes
  REGISTER: { maxRequests: 3, windowMs: 60 * 60 * 1000 }, // 3 attempts per hour
  PASSWORD_RESET: { maxRequests: 3, windowMs: 60 * 60 * 1000 }, // 3 attempts per hour
  
  // Form Submissions
  SUBMIT_STATS: { maxRequests: 30, windowMs: 60 * 1000 }, // 30 per minute
  SUBMIT_PLAYER_STATS: { maxRequests: 50, windowMs: 60 * 1000 }, // 50 per minute
  SUBMIT_ENEMY_STATS: { maxRequests: 20, windowMs: 60 * 1000 }, // 20 per minute
  
  // File Uploads
  UPLOAD_LOGO: { maxRequests: 10, windowMs: 60 * 1000 }, // 10 per minute
  UPLOAD_PLAYER_PICTURE: { maxRequests: 20, windowMs: 60 * 1000 }, // 20 per minute
  
  // Calendar Events
  CREATE_SCRIM_EVENT: { maxRequests: 10, windowMs: 60 * 1000 }, // 10 per minute
  
  // Config Saves
  SAVE_CONFIG: { maxRequests: 20, windowMs: 60 * 1000 }, // 20 per minute (auto-save friendly)
  
  // Firestore Operations
  FIRESTORE_WRITE: { maxRequests: 100, windowMs: 60 * 1000 }, // 100 per minute
  FIRESTORE_READ: { maxRequests: 200, windowMs: 60 * 1000 }, // 200 per minute
  
  // API Calls
  DISCORD_API: { maxRequests: 10, windowMs: 60 * 1000 }, // 10 per minute
  
  // General
  DEFAULT: { maxRequests: 60, windowMs: 60 * 1000 }, // 60 per minute
};

class RateLimiter {
  constructor() {
    this.storage = typeof window !== 'undefined' ? window.localStorage : null;
    this.prefix = 'rate_limit_';
  }

  /**
   * Get rate limit key for storage
   */
  getKey(action) {
    return `${this.prefix}${action}`;
  }

  /**
   * Check if action is rate limited
   * @param {string} action - Action identifier
   * @param {object} config - Optional custom config
   * @returns {object} { allowed: boolean, remaining: number, resetAt: number }
   */
  checkLimit(action, config = null) {
    if (!this.storage) {
      // Server-side or no storage available - allow
      return { allowed: true, remaining: Infinity, resetAt: Date.now() };
    }

    const limit = config || RATE_LIMITS[action] || RATE_LIMITS.DEFAULT;
    const key = this.getKey(action);
    const now = Date.now();

    try {
      const stored = this.storage.getItem(key);
      let data = stored ? JSON.parse(stored) : null;

      // Reset if window expired
      if (!data || now > data.resetAt) {
        data = {
          count: 0,
          resetAt: now + limit.windowMs,
        };
      }

      const remaining = Math.max(0, limit.maxRequests - data.count);
      const allowed = data.count < limit.maxRequests;

      return {
        allowed,
        remaining,
        resetAt: data.resetAt,
        count: data.count,
      };
    } catch (err) {
      console.error('Rate limiter error:', err);
      // On error, allow the request (fail open)
      return { allowed: true, remaining: Infinity, resetAt: now };
    }
  }

  /**
   * Record an action attempt
   * @param {string} action - Action identifier
   * @param {object} config - Optional custom config
   * @returns {boolean} true if allowed, false if rate limited
   */
  record(action, config = null) {
    if (!this.storage) {
      return true;
    }

    const limit = config || RATE_LIMITS[action] || RATE_LIMITS.DEFAULT;
    const key = this.getKey(action);
    const now = Date.now();

    try {
      const stored = this.storage.getItem(key);
      let data = stored ? JSON.parse(stored) : null;

      // Reset if window expired
      if (!data || now > data.resetAt) {
        data = {
          count: 0,
          resetAt: now + limit.windowMs,
        };
      }

      // Check if already at limit
      if (data.count >= limit.maxRequests) {
        return false;
      }

      // Increment count
      data.count += 1;
      this.storage.setItem(key, JSON.stringify(data));

      return true;
    } catch (err) {
      console.error('Rate limiter error:', err);
      return true; // Fail open
    }
  }

  /**
   * Reset rate limit for an action
   */
  reset(action) {
    if (!this.storage) return;
    const key = this.getKey(action);
    this.storage.removeItem(key);
  }

  /**
   * Clear all rate limits (useful for logout)
   */
  clearAll() {
    if (!this.storage) return;
    const keys = Object.keys(this.storage);
    keys.forEach((key) => {
      if (key.startsWith(this.prefix)) {
        this.storage.removeItem(key);
      }
    });
  }

  /**
   * Get remaining time until reset (in ms)
   */
  getTimeUntilReset(action) {
    const check = this.checkLimit(action);
    if (check.resetAt) {
      return Math.max(0, check.resetAt - Date.now());
    }
    return 0;
  }
}

// Singleton instance
export const rateLimiter = new RateLimiter();

/**
 * React Hook for rate limiting
 */
export function useRateLimit(action, config = null) {
  const check = () => rateLimiter.checkLimit(action, config);
  const record = () => rateLimiter.record(action, config);
  const reset = () => rateLimiter.reset(action);
  const timeUntilReset = () => rateLimiter.getTimeUntilReset(action);

  return {
    check,
    record,
    reset,
    timeUntilReset,
    isAllowed: () => check().allowed,
    remaining: () => check().remaining,
  };
}

/**
 * Higher-order function to wrap async functions with rate limiting
 */
export function withRateLimit(action, fn, config = null) {
  return async (...args) => {
    const check = rateLimiter.checkLimit(action, config);
    
    if (!check.allowed) {
      const resetIn = Math.ceil((check.resetAt - Date.now()) / 1000);
      throw new Error(
        `Rate limit exceeded. Please try again in ${resetIn} seconds.`
      );
    }

    if (!rateLimiter.record(action, config)) {
      const resetIn = Math.ceil((check.resetAt - Date.now()) / 1000);
      throw new Error(
        `Rate limit exceeded. Please try again in ${resetIn} seconds.`
      );
    }

    try {
      return await fn(...args);
    } catch (err) {
      // On error, don't count towards rate limit (optional - you may want to count it)
      // rateLimiter.reset(action);
      throw err;
    }
  };
}

/**
 * Format time until reset for display
 */
export function formatTimeUntilReset(ms) {
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`
      : `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
}

